import React from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom-v5-compat";

import Icon from "../app/Atoms/Icons";
import Loading from "./Loading";
import { useJourney } from "./sign_up_journeys";
import { Progress } from "./types";

const TOTAL_MINUTES = 5;

const PercentageComplete = ({ progress }: { progress: Progress }) => {
  const { t } = useTranslation("welcome");
  const { currentStep, totalSteps } = progress;
  const minutesPerPage = TOTAL_MINUTES / totalSteps;
  const percentageComplete = Math.max(
    (currentStep / totalSteps) * 100,
    3,
  ).toFixed(0);
  const minutesRemaining = Math.floor(
    (totalSteps - currentStep) * minutesPerPage,
  );
  return (
    <div className="text-right text-xs">
      {t("common.percentageComplete", { percentageComplete })} -{" "}
      {t("common.minutesRemaining", { minutesRemaining })}
    </div>
  );
};

export const SingleColumnPage = ({
  progress,
  content,
}: {
  progress: Progress;
  content: React.ReactNode;
}) => {
  const { t } = useTranslation("welcome");
  const { loadingState } = useJourney();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen items-stretch sm:flex">
      <div className="flex-1 items-stretch space-y-16 bg-white p-32">
        <div className="flex justify-between">
          <div className="align-middle text-xs">
            <a
              onClick={() => {
                navigate(-1);
              }}
            >
              <Icon
                icon="chevronLeft"
                color={"black"}
                size="16"
                className={undefined}
              />
              {t("common.back")}
            </a>
          </div>
          <PercentageComplete progress={progress} />
        </div>
        <div className="flex justify-center">
          <Loading loadingState={loadingState}>
            <div className="flex-1 px-24">{content} </div>
          </Loading>
        </div>
      </div>
    </div>
  );
};
export const TwoColumnPage = ({
  progress,
  left,
  right,
}: {
  progress: Progress;
  left: React.ReactNode;
  right: React.ReactNode;
}) => {
  const { t } = useTranslation("welcome");
  const { loadingState } = useJourney();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen items-stretch sm:flex">
      <div className="flex-1 items-stretch space-y-16 bg-gradient-to-br from-accent-200 to-accent-500 p-32">
        <div className="align-middle text-xs">
          <a
            onClick={() => {
              navigate(-1);
            }}
          >
            <Icon
              icon="chevronLeft"
              color={"black"}
              size="16"
              className={undefined}
            />
            {t("common.back")}
          </a>
        </div>
        <div className="flex justify-center">
          <div className="flex-1 px-24">{left} </div>
        </div>
      </div>
      <div className="flex-1 space-y-16 bg-white p-32">
        <PercentageComplete progress={progress} />
        <div className="flex justify-center">
          <Loading loadingState={loadingState}>
            <div className="flex-1">{right} </div>
          </Loading>
        </div>
      </div>
    </div>
  );
};

export const ContentBlock = ({
  left,
  right,
  subTitle,
  content,
}: {
  left?: boolean;
  right?: boolean;
  subTitle?: React.ReactNode;
  content?: React.ReactNode[];
}) => {
  return (
    <div className={classNames("flex", { "justify-end": right })}>
      <div
        className={classNames("space-y-4", {
          "w-4/5 text-left": left,
          "w-4/5 text-right": right,
        })}
      >
        {subTitle && <h2>{subTitle}</h2>}
        <div className="space-y-24">
          {(content || []).map((c) => (
            <p>{c}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

export const InfoPage = ({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <div className="mx-auto max-w-lg space-y-32">
      {title && <h1 className="">{title}</h1>}
      <div className="space-y-48">{children}</div>
    </div>
  );
};
export const Testimonial = ({
  name,
  venue,
  quote,
}: {
  name: string;
  venue: string;
  quote: string;
}) => {
  return (
    <div className="space-y-8 px-32 text-xs sm:px-64">
      <div className="pr-24 italic">
        <span className="text-xl leading-none">" </span>
        <span className="leading-loose">{quote}</span>
        <span className="text-xl leading-none"> " </span>
      </div>
      <div className="text-right">
        - {name} - {venue}
      </div>
    </div>
  );
};
